import { useState } from "react";
import axios from "axios";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/** G6: de takken die naar GitHub mogen: alles behalve main, master en de hoofdtak van deze repo. PUUR. */
export function duwbareTakken(takken, hoofd) {
  return (takken || []).filter((b) => b !== "main" && b !== "master" && b !== hoofd);
}

/**
 * G6 (GO Frank 1 november 2026): een tak van deze repo naar GitHub duwen, als reservekopie of om daar een pull request
 * te maken. Eerst de kaart (welke tak, welke vastlegging, welk adres), dan Bevestig met een token van GitHub met
 * schrijfrecht dat je per keer invult en dat nergens bewaard wordt. Nooit de hoofdtak, nooit geforceerd (backend).
 */
export default function RepoNaarGithub({ repo, takken }) {
  const { t } = useTaal();
  const keuzes = duwbareTakken(takken, repo.default_branch);
  const [tak, setTak] = useState(keuzes[0] || "");
  const [kaart, setKaart] = useState(null);
  const [schrijftoken, setSchrijftoken] = useState("");
  const [bezig, setBezig] = useState(false);

  function fout(error, standaard) {
    const detail = error.response?.data?.detail;
    toast.error(typeof detail === "string" ? t(detail) : standaard);
  }

  async function toon() {
    try {
      setKaart((await axios.post(`${API}/repos/${repo.id}/github/duw/toon`, { tak })).data);
    } catch (error) {
      fout(error, t("De kaart kon niet worden getoond."));
    }
  }

  async function bevestig() {
    setBezig(true);
    try {
      await axios.post(`${API}/repos/${repo.id}/github/duw`, { tak: kaart.tak, token: kaart.token, schrijftoken: schrijftoken.trim() });
      toast.success(t("Tak {naam} staat op GitHub.", { naam: kaart.tak }));
    } catch (error) {
      fout(error, t("Duwen naar GitHub lukte niet."));
    } finally {
      // Het token blijft alleen in dit veld tot het verzoek weg is; de kaart is eenmalig.
      setSchrijftoken("");
      setKaart(null);
      setBezig(false);
    }
  }

  if (!keuzes.length) {
    return <p className="text-xs text-muted-foreground" data-testid="naar-github-geen">{t("Er is geen tak om naar GitHub te duwen: de hoofdtak gaat daar nooit heen.")}</p>;
  }
  return (
    <section className="space-y-2 rounded-lg border p-3" data-testid="naar-github">
      <h3 className="text-sm font-semibold">{t("Een tak naar GitHub duwen")}</h3>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select value={tak} onChange={(e) => { setTak(e.target.value); setKaart(null); }} aria-label={t("Tak")}
                className="rounded-md border bg-transparent px-1 py-1" data-testid="naar-github-tak">
          {keuzes.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <Button type="button" size="sm" variant="outline" className="tap-target" onClick={toon} data-testid="naar-github-toon">
          {t("Toon wat er gebeurt")}
        </Button>
      </div>
      {kaart && (
        <div className="space-y-2 rounded-md border border-amber-500/40 p-2 text-sm" data-testid="naar-github-kaart">
          <p>{t("Tak {tak} op vastlegging {sha} gaat naar {adres}. Niet geforceerd: staat daar iets wat hier niet is, dan weigert GitHub.", { tak: kaart.tak, sha: kaart.kort, adres: kaart.adres })}</p>
          <input type="password" autoComplete="off" value={schrijftoken} onChange={(e) => setSchrijftoken(e.target.value)}
                 placeholder={t("Token van GitHub met schrijfrecht")} aria-label={t("Token van GitHub met schrijfrecht")}
                 className="w-full rounded-md border bg-transparent px-2 py-1" data-testid="naar-github-token" />
          <Button type="button" size="sm" className="tap-target" disabled={bezig || !schrijftoken.trim()} onClick={bevestig}
                  data-testid="naar-github-bevestig">
            {bezig ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{t("Bevestig")}
          </Button>
        </div>
      )}
    </section>
  );
}
